import { useId, type ReactNode } from "react";

export function ProductPage({
  children,
  className = "",
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <section className={`pf-page-surface pf-page-inner ${className}`.trim()}>{children}</section>
  );
}

export function ProductHeading({
  eyebrow,
  title,
  body,
  actions,
  titleId,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly body: ReactNode;
  readonly actions?: ReactNode;
  readonly titleId?: string;
}) {
  return (
    <header className="pf-page-heading pf-heading">
      <div>
        <p className="pf-kicker">{eyebrow}</p>
        <h1 id={titleId}>{title}</h1>
        <div className="pf-heading-copy">{body}</div>
      </div>
      {actions === undefined ? null : <div className="pf-heading-actions">{actions}</div>}
    </header>
  );
}

export function ProductPanel({
  title,
  eyebrow,
  meta,
  children,
  className = "",
  titleId,
}: {
  readonly title?: string;
  readonly eyebrow?: string;
  readonly meta?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  readonly titleId?: string;
}) {
  const generatedTitleId = useId();
  const headingId = title === undefined ? undefined : (titleId ?? generatedTitleId);
  return (
    <section
      className={`pf-product-panel pf-panel ${className}`.trim()}
      aria-labelledby={headingId}
    >
      {title === undefined && eyebrow === undefined && meta === undefined ? null : (
        <header className="pf-panel-heading pf-panel-head">
          <div>
            {eyebrow === undefined ? null : <p className="pf-kicker">{eyebrow}</p>}
            {title === undefined ? null : <h2 id={headingId}>{title}</h2>}
          </div>
          {meta === undefined ? null : <div className="pf-panel-meta">{meta}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function ProductMetric({
  label,
  value,
  detail,
  tone = "neutral",
  onClick,
  selected = false,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly detail?: ReactNode;
  readonly tone?: "neutral" | "positive" | "warning" | "negative";
  readonly onClick?: () => void;
  readonly selected?: boolean;
}) {
  const contents = (
    <>
      <span>{label}</span>
      <strong>{value}</strong>
      {detail === undefined ? null : <small>{detail}</small>}
    </>
  );
  return onClick === undefined ? (
    <article className={`pf-product-metric pf-stat pf-tone-${tone}`}>{contents}</article>
  ) : (
    <button
      type="button"
      className={`pf-product-metric pf-stat pf-tone-${tone}${selected ? " is-selected" : ""}`}
      aria-pressed={selected}
      onClick={onClick}
    >
      {contents}
    </button>
  );
}

export function ProgressiveDisclosure({
  label,
  summary,
  children,
  open,
}: {
  readonly label: string;
  readonly summary?: string;
  readonly children: ReactNode;
  readonly open?: boolean;
}) {
  return (
    <details className="pf-progressive" open={open}>
      <summary>
        <span>{label}</span>
        {summary === undefined ? null : <small>{summary}</small>}
      </summary>
      <div className="pf-progressive-content">{children}</div>
    </details>
  );
}

export function StatusDot({
  label,
  tone = "live",
}: {
  readonly label: string;
  readonly tone?: "live" | "warning" | "danger" | "muted";
}) {
  return <span className={`pf-status-dot pf-status-${tone}`}>{label}</span>;
}
